import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { CheckCircle2, Loader2, Rocket, ArrowLeft, ArrowRight } from "lucide-react";
import { DEPLOY } from "@/constants/testIds";
import DeviceSelectStep from "@/components/app/DeviceSelectStep";

// Coerce any value to a printable string so an accidentally-nested object
// (e.g. address returned as `{line1: ..., city: ...}` from a subsidiary
// tenant) doesn't crash the whole page via React's "objects are not valid
// as a child" rule.
const toText = (v) => {
  if (v == null) return "";
  if (typeof v === "string" || typeof v === "number" || typeof v === "boolean") return String(v);
  // For objects/arrays, prefer the common human-readable fields; fall back to JSON.
  if (typeof v === "object") {
    const parts = [v.address_line1, v.address_line2, v.city, v.state, v.country, v.postal_code, v.pincode]
      .filter((p) => typeof p === "string" && p.trim());
    if (parts.length) return parts.join(", ");
    try { return JSON.stringify(v); } catch { return String(v); }
  }
  return String(v);
};
import StatusBadge from "@/components/app/StatusBadge";
import { CustomerEntitySelector } from "@/components/app/CustomerEntitySelector";

const SERVICE_GROUPS = [
  {
    id: "prep", label: "Device Preparation",
    services: [
      { code: "configuration",       label: "Device Configuration" },
      { code: "quality_check",       label: "Pre-Deployment Quality Check" },
      { code: "asset_tagging",       label: "Asset Tagging & Labelling" },
      { code: "security_hardening",  label: "Security Configuration / Hardening" },
      { code: "data_migration",      label: "Data Migration / User Profile Transfer" },
      { code: "user_setup",          label: "User Setup / Handover Assistance" },
      { code: "custom_configuration",label: "Custom Configuration" },
    ],
  },
  {
    id: "mgmt", label: "Device Management & Software",
    services: [
      // Apple / cross-platform MDM
      { code: "mdm_enrollment",        label: "MDM / UEM Enrolment",             family: "any" },
      { code: "abm_assignment",        label: "Apple Business Manager Assignment", family: "apple" },
      // Windows
      { code: "autopilot_registration",label: "Windows Autopilot Registration",  family: "windows" },
      { code: "autopilot_deployment",  label: "Windows Autopilot Deployment",    family: "windows" },
      { code: "autopilot_pre_provisioning", label: "Autopilot Pre-Provisioning", family: "windows" },
      { code: "autopilot_device_prep", label: "Autopilot Device Preparation",    family: "windows" },
      { code: "intune_enrolment",      label: "Microsoft Intune Enrolment",      family: "windows" },
      { code: "entra_join",            label: "Microsoft Entra Join",            family: "windows" },
      { code: "entra_hybrid_join",     label: "Microsoft Entra Hybrid Join",     family: "windows" },
      // Common software
      { code: "software_installation", label: "Software Installation" },
      { code: "m365_apps",             label: "Microsoft 365 Applications",      family: "windows" },
      { code: "security_config",       label: "Security Configuration" },
      { code: "bitlocker",             label: "BitLocker Configuration",         family: "windows" },
      { code: "defender",              label: "Microsoft Defender Configuration",family: "windows" },
    ],
  },
  {
    id: "accessories", label: "Accessories & Packaging",
    services: [
      { code: "accessories",         label: "Accessories" },
      { code: "standard_packaging",  label: "Standard Packaging" },
      { code: "premium_packaging",   label: "Premium Packaging" },
      { code: "branded_packaging",   label: "Customer-Branded Packaging" },
      { code: "eco_packaging",       label: "Eco-Friendly Packaging" },
      { code: "welcome_kit",         label: "Welcome Kit / Employee Kit" },
      { code: "special_handling",    label: "Special Handling" },
    ],
  },
  {
    id: "protection", label: "Protection, Warranty & Insurance",
    services: [
      { code: "applecare",              label: "AppleCare & Apple Protection", family: "apple" },
      { code: "oem_warranty",           label: "OEM Extended Warranty" },
      { code: "accidental_damage",      label: "Accidental Damage Protection" },
      { code: "third_party_insurance",  label: "Third-Party Device Insurance" },
      { code: "transit_insurance",      label: "Shipment / Transit Insurance" },
      { code: "customer_provided",      label: "Customer-Provided Coverage" },
      { code: "no_coverage",            label: "No Additional Coverage Required" },
    ],
  },
];

const APPLECARE_PLANS = [
  { v: "APPLECARE_PLUS",              l: "AppleCare+" },
  { v: "APPLECARE_PLUS_BUSINESS",     l: "AppleCare+ for Business" },
  { v: "APPLECARE_FOR_ENTERPRISE",    l: "AppleCare for Enterprise" },
  { v: "APPLECARE_PLUS_THEFT_LOSS",   l: "AppleCare+ with Theft & Loss (where eligible)" },
  { v: "EXISTING_APPLECARE",          l: "Existing AppleCare Coverage" },
  { v: "OTHER_APPLE_COVERAGE",        l: "Other Approved Apple Coverage" },
];

const INSURANCE_ARRANGEMENTS = [
  { v: "NEW_INSURANCE_THROUGH_SUNTRONIX", l: "New insurance through Suntronix" },
  { v: "EXISTING_CUSTOMER_POLICY",        l: "Existing customer policy" },
  { v: "MASTER_CLIENT_POLICY",            l: "Master client policy" },
  { v: "CUSTOMER_NOMINATED_INSURER",      l: "Customer-nominated insurer" },
  { v: "OTHER_APPROVED",                  l: "Other approved arrangement" },
];

const AUTOPILOT_SERVICES = [
  "REGISTER_DEVICE_ONLY", "VERIFY_EXISTING_REGISTRATION",
  "REGISTER_AND_ASSIGN_PROFILE", "REGISTER_ASSIGN_AND_PRE_PROVISION",
  "FULL_AUTOPILOT_DEPLOYMENT", "AUTOPILOT_DEVICE_PREPARATION",
  "REMOVE_AND_RE_REGISTER",
];

const REGISTRATION_METHODS = [
  "OEM_REGISTRATION", "DISTRIBUTOR_REGISTRATION", "RESELLER_CSP_PARTNER",
  "CUSTOMER_INTUNE_IMPORT", "CUSTOMER_PROVIDED_CSV",
  "MICROSOFT_GRAPH_INTEGRATION", "HARDWARE_HASH_IMPORT",
  "PKID_OEM_DEVICE_ID", "EXISTING_AUTOPILOT_REGISTRATION",
];

const DEPLOYMENT_METHODS = ["USER_DRIVEN", "PRE_PROVISIONED", "SELF_DEPLOYING",
  "EXISTING_DEVICE_DEPLOYMENT", "AUTOPILOT_DEVICE_PREPARATION"];

const ENTRA_JOIN_TYPES = ["ENTRA_JOINED", "ENTRA_HYBRID_JOINED",
  "EXISTING_JOIN_CONFIGURATION", "NOT_APPLICABLE"];

const AUTHORISATION_STATUSES = ["NOT_REQUIRED", "REQUIRED", "REQUESTED",
  "CUSTOMER_ACTION_PENDING", "AUTHORISED", "AUTHORISED_WITH_CONDITIONS"];

const STEPS = ["Master Company", "Employee", "Device", "Services", "Review"];

export default function DeployDevice() {
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [masterCompanies, setMasterCompanies] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [subsLoaded, setSubsLoaded] = useState(false);
  const [companyLabel, setCompanyLabel] = useState("");
  const [employees, setEmployees] = useState([]);
  const [empQuery, setEmpQuery] = useState("");
  const [assets, setAssets] = useState([]);

  const [form, setForm] = useState({
    deployment_target: "EMPLOYEE",
    deployment_purpose: "",
    customer_location_id: "",
    receiving_contact_name: "",
    receiving_contact_phone: "",
    receiving_contact_email: "",
    custodian_name: "",
    custodian_email: "",
    master_company_id: "",
    company_id: "",
    employee_id: "",
    asset_ids: [],
    services_selected: [],
    priority: "normal",
    address: "",
    address_source: "verified_employee",   // verified_employee|customer_office|approved_alternate|new_address
    delivery_contact_name: "",
    delivery_contact_phone: "",
    secondary_contact_name: "",
    secondary_contact_phone: "",
    secondary_contact_relationship: "",
    secondary_contact_permission: false,
    notes: "",
    // AppleCare
    applecare_plan: "",
    applecare_source: "",
    apple_agreement_number: "",
    applecare_existing_proof_url: "",
    // Third-party insurance
    insurance_arrangement: "",
    insurance_provider: "",
    insurance_policy_number: "",
    insurance_sum_insured: "",
    insurance_coverage_types: [],
    // Transit insurance
    transit_declared_value: "",
    transit_insured_value: "",
    transit_policy_number: "",
    transit_certificate_url: "",
    // Customer-provided
    customer_provided_provider: "",
    customer_provided_policy: "",
    // No coverage
    no_coverage_reason: "",
    no_coverage_approved_by: "",
    // Windows / Autopilot / Intune / Entra
    ms_tenant_pick: "",         // ID of selected microsoft_tenant record
    ms_tenant_id: "",
    ms_primary_domain: "",
    autopilot_service_required: "",
    registration_method: "",
    deployment_method: "",
    entra_join_type: "",
    autopilot_profile_id: "",
    autopilot_group_tag: "",
    user_upn: "",
    customer_authorisation_status: "NOT_REQUIRED",
    hardware_hash_status: "NOT_REQUIRED",
    pre_provisioning_status: "NOT_STARTED",
    // Coverage arranged/paid
    coverage_arranged_by: "SUNTRONIX",
    coverage_paid_by: "CUSTOMER",
  });
  const [submitting, setSubmitting] = useState(false);
  const [created, setCreated] = useState(null);
  const [preflight, setPreflight] = useState(null);
  const [appleAgreements, setAppleAgreements] = useState([]);
  const [msTenants, setMsTenants] = useState([]);
  const [redeploy, setRedeploy] = useState(false);
  const [deviceReload, setDeviceReload] = useState(0);

  useEffect(() => {
    api.get("/master-companies").then(({ data }) => {
      const items = data.items || [];
      setMasterCompanies(items);
      // Customer logins typically have a single master company — auto-select it
      // so the subsidiary list populates immediately (no empty/partial dropdown).
      if (items.length === 1) {
        setForm((f) => (f.master_company_id ? f : { ...f, master_company_id: items[0].id }));
      }
    });
  }, []);

  useEffect(() => {
    if (!form.master_company_id) { setAppleAgreements([]); setMsTenants([]); return; }
    api.get("/deployment-services/apple-care-agreements",
      { params: { master_company_id: form.master_company_id } })
      .then(({ data }) => setAppleAgreements(data.items || [])).catch(() => {});
    api.get("/deployment-services/microsoft-tenants",
      { params: { master_company_id: form.master_company_id } })
      .then(({ data }) => setMsTenants(data.items || [])).catch(() => {});
  }, [form.master_company_id]);

  useEffect(() => {
    if (!form.master_company_id) {
      setCompanies([]);
      setSubsLoaded(false);
      return;
    }
    setSubsLoaded(false);
    api
      .get("/companies", { params: { master_company_id: form.master_company_id } })
      .then(({ data }) => { setCompanies(data.items || []); setSubsLoaded(true); })
      .catch(() => setSubsLoaded(true));
  }, [form.master_company_id]);

  // Some master companies (e.g. REMOTE) have no subsidiaries — hide the selector.
  const hasSubsidiaries = companies.length > 0;
  const noSubsidiaries = subsLoaded && companies.length === 0;

  useEffect(() => {
    // Employees are keyed by subsidiary; when a master company has no subsidiary,
    // fall back to master-company-level employees so the flow still works.
    if (form.company_id) {
      api.get("/employees", { params: { company_id: form.company_id, q: empQuery || undefined, limit: 25 } })
        .then(({ data }) => setEmployees(data.items || []));
    } else if (noSubsidiaries && form.master_company_id) {
      api.get("/employees", { params: { master_company_id: form.master_company_id, q: empQuery || undefined, limit: 25 } })
        .then(({ data }) => setEmployees(data.items || []));
    } else {
      setEmployees([]);
    }
  }, [form.company_id, form.master_company_id, noSubsidiaries, empQuery]);

  useEffect(() => {
    // Fetch full details for any selected device not already cached, so the
    // Services + Review steps always reflect the exact chosen assets (the rich
    // Select Device step drives selection now).
    const missing = form.asset_ids.filter((id) => !assets.some((a) => a.id === id));
    if (missing.length === 0) return;
    (async () => {
      const fetched = await Promise.all(missing.map((id) =>
        api.get(`/assets/${id}`).then(({ data }) => data).catch(() => null)));
      const ok = fetched.filter(Boolean);
      if (ok.length) setAssets((prev) => [...prev, ...ok]);
    })();
  }, [form.asset_ids, assets]);

  const selectedEmployee = useMemo(
    () => employees.find((e) => e.id === form.employee_id),
    [employees, form.employee_id]
  );
  const selectedAssets = assets.filter((a) => form.asset_ids.includes(a.id));

  const canNext = () => {
    if (step === 0) {
      if (!form.master_company_id) return false;
      if (hasSubsidiaries && !form.company_id) return false;
      if (form.deployment_target !== "EMPLOYEE") {
        // Non-employee targets need location + receiving contact
        if (["CUSTOMER_OFFICE", "CUSTOMER_WAREHOUSE", "PROJECT_SITE",
             "MEETING_ROOM", "TRAINING_ROOM", "IT_STOCK_AT_CUSTOMER_LOCATION",
             "LOANER_POOL"].includes(form.deployment_target)
            && !form.customer_location_id) return false;
        if (!form.receiving_contact_name || !form.receiving_contact_phone) return false;
      }
      return true;
    }
    if (step === 1) {
      if (form.deployment_target !== "EMPLOYEE") return true;   // skip employee step
      return !!form.employee_id;
    }
    if (step === 2) return form.asset_ids.length > 0;
    return true;
  };

  const toggleAsset = (id) => {
    setForm((f) => ({
      ...f,
      asset_ids: f.asset_ids.includes(id) ? f.asset_ids.filter((x) => x !== id) : [...f.asset_ids, id],
    }));
  };
  const toggleService = (code) => {
    setForm((f) => ({
      ...f,
      services_selected: f.services_selected.includes(code)
        ? f.services_selected.filter((x) => x !== code)
        : [...f.services_selected, code],
    }));
  };

  const runPreflight = async () => {
    const primaryAsset = selectedAssets[0];
    const deviceType = (primaryAsset?.category || primaryAsset?.brand || "").toLowerCase().includes("apple")
      || (primaryAsset?.brand || "").toLowerCase().startsWith("apple")
      || (primaryAsset?.brand || "").toLowerCase() === "apple"
      ? "mac"
      : ((primaryAsset?.brand || "").toLowerCase().includes("microsoft")
        || (primaryAsset?.model || "").toLowerCase().includes("windows")
        || (primaryAsset?.category || "").toLowerCase().includes("windows"))
      ? "windows" : (primaryAsset?.category || "windows");
    try {
      const { data } = await api.post("/deployment-services/preflight", {
        master_company_id: form.master_company_id,
        legal_entity_id: null,
        country: selectedEmployee?.country || "IN",
        device_type: deviceType,
        device_serial: primaryAsset?.serial || null,
        services_selected: form.services_selected,
        applecare_plan: form.applecare_plan || null,
        apple_agreement_number: form.apple_agreement_number || null,
        applecare_existing_proof_url: form.applecare_existing_proof_url || null,
        insurance_arrangement: form.insurance_arrangement || null,
        insurance_provider: form.insurance_provider || null,
        insurance_policy_number: form.insurance_policy_number || null,
        insurance_sum_insured: form.insurance_sum_insured ? Number(form.insurance_sum_insured) : null,
        transit_declared_value: form.transit_declared_value ? Number(form.transit_declared_value) : null,
        transit_insured_value: form.transit_insured_value ? Number(form.transit_insured_value) : null,
        transit_policy_number: form.transit_policy_number || null,
        no_coverage_reason: form.no_coverage_reason || null,
        no_coverage_approved_by: form.no_coverage_approved_by || null,
        ms_tenant_id: form.ms_tenant_id || null,
        ms_primary_domain: form.ms_primary_domain || null,
        autopilot_service_required: form.autopilot_service_required || null,
        registration_method: form.registration_method || null,
        deployment_method: form.deployment_method || null,
        entra_join_type: form.entra_join_type || null,
        autopilot_profile_id: form.autopilot_profile_id || null,
        user_upn: form.user_upn || null,
        customer_authorisation_status: form.customer_authorisation_status || "NOT_REQUIRED",
        pre_provisioning_status: form.pre_provisioning_status || null,
      });
      setPreflight(data);
      return data;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Preflight failed");
      return null;
    }
  };

  const handleContinue = async () => {
    if (!canNext()) return;
    // Auto-skip Employee step (step 1) when deployment target is non-employee
    if (step === 0 && form.deployment_target !== "EMPLOYEE") {
      setStep(2);
      return;
    }
    if (step === 3) {
      const res = await runPreflight();
      if (res && !res.ok) {
        toast.error(`Preflight blocked: ${res.blockers.length} issue(s)`);
        return;
      }
    }
    setStep((s) => s + 1);
  };

  const submit = async () => {
    setSubmitting(true);
    try {
      // For non-employee targets, create an office deployment batch and skip the request flow
      if (form.deployment_target !== "EMPLOYEE") {
        const { data } = await api.post("/office-deployment/batches", {
          master_company_id: form.master_company_id,
          deployment_target: form.deployment_target,
          deployment_purpose: form.deployment_purpose || null,
          customer_location_id: form.customer_location_id || null,
          asset_ids: form.asset_ids,
          receiving_contact: {
            name: form.receiving_contact_name,
            phone: form.receiving_contact_phone,
            email: form.receiving_contact_email || null,
          },
          custodian: {
            name: form.custodian_name || null,
            email: form.custodian_email || null,
          },
          services_selected: form.services_selected,
          notes: form.notes,
        });
        setCreated({ request_code: data.item.code, status: data.item.status });
        toast.success(`Office batch ${data.item.code} created`);
        return;
      }
      const payload = {
        request_type: "deploy_device",
        service_name: "Deploy Device",
        master_company_id: form.master_company_id,
        company_id: form.company_id || null,
        employee_id: form.employee_id,
        asset_ids: form.asset_ids,
        redeploy: redeploy,
        priority: form.priority,
        country: selectedEmployee?.country,
        address: form.address || selectedEmployee?.delivery_address,
        delivery_contact_name: form.delivery_contact_name || selectedEmployee?.name,
        delivery_contact_phone: form.delivery_contact_phone || selectedEmployee?.phone,
        services_selected: form.services_selected,
        service_context: {
          applecare_plan: form.applecare_plan,
          apple_agreement_number: form.apple_agreement_number,
          insurance_arrangement: form.insurance_arrangement,
          insurance_provider: form.insurance_provider,
          insurance_policy_number: form.insurance_policy_number,
          transit_declared_value: form.transit_declared_value,
          transit_insured_value: form.transit_insured_value,
          transit_policy_number: form.transit_policy_number,
          ms_tenant_id: form.ms_tenant_id,
          ms_primary_domain: form.ms_primary_domain,
          autopilot_service_required: form.autopilot_service_required,
          registration_method: form.registration_method,
          deployment_method: form.deployment_method,
          entra_join_type: form.entra_join_type,
          autopilot_profile_id: form.autopilot_profile_id,
          user_upn: form.user_upn,
          customer_authorisation_status: form.customer_authorisation_status,
          coverage_arranged_by: form.coverage_arranged_by,
          coverage_paid_by: form.coverage_paid_by,
          address_source: form.address_source,
          secondary_contact_name: form.secondary_contact_name,
          secondary_contact_phone: form.secondary_contact_phone,
          secondary_contact_relationship: form.secondary_contact_relationship,
          secondary_contact_permission: form.secondary_contact_permission,
        },
        notes: form.notes,
      };
      const { data } = await api.post("/requests/", payload);
      setCreated(data);
      toast.success(`Request ${data.request_code} created`);
    } catch (e) {
      if (e.response?.status === 409) {
        toast.error("Device no longer available — refreshing the list.");
        setForm((f) => ({ ...f, asset_ids: [] }));
        setStep(2);
        setDeviceReload((n) => n + 1);
        return;
      }
      toast.error(typeof e.response?.data?.detail === "string"
        ? e.response.data.detail
        : (e.response?.data?.detail ? JSON.stringify(e.response.data.detail) : "Failed to submit request"));
    } finally {
      setSubmitting(false);
    }
  };

  if (created) {
    return (
      <div className="max-w-2xl bg-white border border-slate-200 rounded-md p-8 text-center animate-fade-in">
        <div className="mx-auto h-12 w-12 rounded-full bg-emerald-100 grid place-items-center">
          <CheckCircle2 className="h-6 w-6 text-emerald-700" />
        </div>
        <h2 className="mt-4 text-xl font-heading font-semibold text-slate-900">Deployment Request Submitted</h2>
        <p className="mt-1 text-sm text-slate-500">Reference number for tracking</p>
        <div className="mt-3 font-mono text-lg font-semibold text-slate-900">{created.request_code}</div>
        <div className="mt-2 inline-block"><StatusBadge value={created.status} /></div>
        <div className="mt-8 flex items-center justify-center gap-2">
          <Button variant="outline" onClick={() => navigate("/requests")}>View all requests</Button>
          <Button className="bg-slate-900" onClick={() => navigate(`/requests`)}>Track request</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl">
      <div className="mb-6">
        <div className="text-[10px] font-semibold tracking-[0.08em] uppercase text-slate-500">
          Lifecycle · Self-Service
        </div>
        <h1 className="text-2xl font-heading font-semibold text-slate-900 tracking-tight">
          Deploy Device
        </h1>
        <p className="text-sm text-slate-500 mt-0.5">
          Ship a configured device from warehouse inventory to an employee.
        </p>
      </div>

      {/* Stepper */}
      <div className="grid grid-cols-5 gap-2 mb-6">
        {STEPS.map((s, i) => (
          <div
            key={s}
            className={`text-center border-t-2 pt-2 text-xs font-medium tracking-tight ${
              i <= step ? "border-slate-900 text-slate-900" : "border-slate-200 text-slate-400"
            }`}
          >
            <div className="text-[10px] uppercase tracking-[0.08em]">Step {i + 1}</div>
            <div>{s}</div>
          </div>
        ))}
      </div>

      <div className="bg-white border border-slate-200 rounded-md p-6">
        {step === 0 && (
          <div className="space-y-4" data-testid={DEPLOY.stepMasterCo}>
            <div>
              <Label className="text-xs font-semibold text-slate-700">Master Company</Label>
              <Select
                value={form.master_company_id}
                onValueChange={(v) => { setForm({ ...form, master_company_id: v, company_id: "", employee_id: "" }); setCompanyLabel(""); }}
              >
                <SelectTrigger className="h-10 mt-1"><SelectValue placeholder="Select master company…" /></SelectTrigger>
                <SelectContent>
                  {masterCompanies.map((m) => (
                    <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {!noSubsidiaries && (
            <div>
              <Label className="text-xs font-semibold text-slate-700">Subsidiary / Entity</Label>
              <div className="mt-1">
                <CustomerEntitySelector
                  parentId={form.master_company_id || null}
                  parentName={masterCompanies.find((m) => m.id === form.master_company_id)?.name}
                  value={form.company_id}
                  valueLabel={companyLabel}
                  allowParentDirect={false}
                  sourceFilter={["company"]}
                  onChange={(id, opt) => {
                    setForm((f) => ({ ...f, company_id: id, employee_id: "" }));
                    setCompanyLabel(opt?.label || "");
                  }}
                  testId="deploy-subsidiary-selector"
                />
              </div>
              <p className="text-[11px] text-slate-500 mt-1">
                Resolved from the customer hierarchy — pick the exact subsidiary this deployment belongs to.
              </p>
            </div>
            )}
          </div>
        )}

        {step === 1 && (
          <div className="space-y-4" data-testid={DEPLOY.stepEmployee}>
            <Input
              placeholder="Search employee by name, email or code…"
              value={empQuery}
              onChange={(e) => setEmpQuery(e.target.value)}
              className="h-10"
            />
            <Select
              value={form.employee_id}
              onValueChange={(v) => setForm({ ...form, employee_id: v })}
            >
              <SelectTrigger data-testid={DEPLOY.employeeSelect} className="h-10">
                <SelectValue placeholder="Select employee…" />
              </SelectTrigger>
              <SelectContent className="max-h-72">
                {employees.map((e) => (
                  <SelectItem key={e.id} value={e.id}>{e.name} · {e.email}</SelectItem>
                ))}
              </SelectContent>
            </Select>

            {selectedEmployee && (
              <div className="border border-slate-200 rounded-md p-4 grid grid-cols-2 gap-3 text-sm">
                <div><span className="text-slate-500">Code</span><div className="font-mono">{toText(selectedEmployee.employee_code)}</div></div>
                <div><span className="text-slate-500">Department</span><div>{toText(selectedEmployee.department)}</div></div>
                <div><span className="text-slate-500">Phone</span><div>{toText(selectedEmployee.phone)}</div></div>
                <div><span className="text-slate-500">Country</span><div>{toText(selectedEmployee.country)}</div></div>
                <div className="col-span-2"><span className="text-slate-500">Delivery Address</span><div>{toText(selectedEmployee.delivery_address)}</div></div>
              </div>
            )}
          </div>
        )}

        {step === 2 && (
          <div data-testid={DEPLOY.stepDevice}>
            <DeviceSelectStep
              masterCompanyId={form.master_company_id}
              companyId={form.company_id}
              selectedIds={form.asset_ids}
              selectedAssets={selectedAssets}
              onToggle={toggleAsset}
              redeploy={redeploy}
              setRedeploy={setRedeploy}
              reloadSignal={deviceReload}
            />
          </div>
        )}

        {step === 3 && (
          <div className="space-y-6" data-testid={DEPLOY.stepServices}>
            {SERVICE_GROUPS.map((grp) => (
              <ServiceGroup
                key={grp.id}
                group={grp}
                selectedAssets={selectedAssets}
                form={form}
                setForm={setForm}
                toggleService={toggleService}
                appleAgreements={appleAgreements}
                msTenants={msTenants}
              />
            ))}

            {/* ---------- Delivery ---------- */}
            <div className="border border-slate-200 rounded-md bg-white p-4 space-y-4"
              data-testid="deploy-delivery-section">
              <div className="text-[11px] font-semibold tracking-[0.14em] uppercase text-slate-500">
                Delivery
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs">Priority</Label>
                  <Select value={form.priority} onValueChange={(v) => setForm({ ...form, priority: v })}>
                    <SelectTrigger className="h-10 mt-1" data-testid="deploy-priority"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="normal">Normal</SelectItem>
                      <SelectItem value="high">High</SelectItem>
                      <SelectItem value="urgent">Urgent</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs">Delivery address source</Label>
                  <Select value={form.address_source}
                    onValueChange={(v) => setForm({ ...form, address_source: v,
                      address: v === "new_address" ? "" : form.address })}>
                    <SelectTrigger className="h-10 mt-1" data-testid="deploy-address-source"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="verified_employee">Use verified employee address</SelectItem>
                      <SelectItem value="customer_office">Use customer office address</SelectItem>
                      <SelectItem value="approved_alternate">Use approved alternate address</SelectItem>
                      <SelectItem value="new_address">Enter new delivery address</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs">Primary delivery contact name</Label>
                  <Input className="h-10 mt-1"
                    value={form.delivery_contact_name}
                    onChange={(e) => setForm({ ...form, delivery_contact_name: e.target.value })}
                    placeholder={selectedEmployee?.name || ""}
                    data-testid="deploy-primary-name" />
                </div>
                <div>
                  <Label className="text-xs">Primary delivery contact number</Label>
                  <Input className="h-10 mt-1"
                    value={form.delivery_contact_phone}
                    onChange={(e) => setForm({ ...form, delivery_contact_phone: e.target.value })}
                    placeholder={selectedEmployee?.phone || ""}
                    data-testid="deploy-primary-phone" />
                </div>
                <div>
                  <Label className="text-xs">Secondary contact name</Label>
                  <Input className="h-10 mt-1"
                    value={form.secondary_contact_name}
                    onChange={(e) => setForm({ ...form, secondary_contact_name: e.target.value })}
                    data-testid="deploy-secondary-name" />
                </div>
                <div>
                  <Label className="text-xs">Secondary contact number</Label>
                  <Input className="h-10 mt-1"
                    value={form.secondary_contact_phone}
                    onChange={(e) => setForm({ ...form, secondary_contact_phone: e.target.value })}
                    data-testid="deploy-secondary-phone" />
                </div>
                <div>
                  <Label className="text-xs">Relationship</Label>
                  <Input className="h-10 mt-1"
                    value={form.secondary_contact_relationship}
                    onChange={(e) => setForm({ ...form, secondary_contact_relationship: e.target.value })}
                    placeholder="Spouse / Colleague / Manager" />
                </div>
                <label className="flex items-end gap-2 pb-2">
                  <Checkbox checked={form.secondary_contact_permission}
                    onCheckedChange={(v) => setForm({ ...form, secondary_contact_permission: !!v })}
                    data-testid="deploy-secondary-permission" />
                  <span className="text-xs text-slate-700">Permission to contact secondary contact</span>
                </label>
              </div>
              {form.address_source === "new_address" && (
                <div>
                  <Label className="text-xs">New delivery address (re-verification required)</Label>
                  <Textarea className="mt-1"
                    value={form.address}
                    onChange={(e) => setForm({ ...form, address: e.target.value })}
                    data-testid="deploy-new-address" />
                  <div className="text-[11px] text-amber-700 mt-1">
                    CHANGED — RE-VERIFICATION REQUIRED
                  </div>
                </div>
              )}
              <div>
                <Label className="text-xs">Notes</Label>
                <Textarea className="mt-1"
                  value={form.notes}
                  onChange={(e) => setForm({ ...form, notes: e.target.value })}
                  placeholder="Any special instructions…" />
              </div>
            </div>

            {/* ---------- Coverage responsibility ---------- */}
            <div className="border border-slate-200 rounded-md bg-white p-4 grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Coverage arranged by</Label>
                <Select value={form.coverage_arranged_by}
                  onValueChange={(v) => setForm({ ...form, coverage_arranged_by: v })}>
                  <SelectTrigger className="h-10 mt-1" data-testid="coverage-arranged-by"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {["SUNTRONIX", "MASTER_CLIENT", "CUSTOMER", "EMPLOYEE",
                      "OEM", "INSURANCE_PROVIDER", "OTHER"].map((v) =>
                      <SelectItem key={v} value={v}>{v.replace(/_/g, " ")}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">Coverage paid by</Label>
                <Select value={form.coverage_paid_by}
                  onValueChange={(v) => setForm({ ...form, coverage_paid_by: v })}>
                  <SelectTrigger className="h-10 mt-1" data-testid="coverage-paid-by"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {["SUNTRONIX", "MASTER_CLIENT", "CUSTOMER",
                      "INCLUDED_IN_CONTRACT", "RECHARGEABLE_TO_CUSTOMER", "OTHER"].map((v) =>
                      <SelectItem key={v} value={v}>{v.replace(/_/g, " ")}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {preflight && (
              <PreflightPanel result={preflight} onDismiss={() => setPreflight(null)} />
            )}
          </div>
        )}

        {step === 4 && (
          <div className="space-y-4" data-testid={DEPLOY.stepReview}>
            <ReviewLine label="Employee" value={selectedEmployee ? `${selectedEmployee.name} · ${selectedEmployee.email}` : "—"} />
            <ReviewLine label="Company" value={companies.find((c) => c.id === form.company_id)?.name} />
            <ReviewLine
              label="Devices"
              value={
                selectedAssets.length
                  ? selectedAssets.map((a) => `${a.brand} ${a.model} (${a.serial})`).join(", ")
                  : "—"
              }
            />
            <ReviewLine label="Services" value={form.services_selected.length ? form.services_selected.join(", ") : "None"} />
            <ReviewLine label="Priority" value={form.priority} />
            <ReviewLine label="Address" value={toText(form.address) || toText(selectedEmployee?.delivery_address)} />
          </div>
        )}

        <div className="mt-6 flex items-center justify-between border-t border-slate-100 pt-4">
          <Button
            variant="outline"
            data-testid={DEPLOY.backBtn}
            onClick={() => setStep((s) => Math.max(0, s - 1))}
            disabled={step === 0 || submitting}
          >
            <ArrowLeft className="h-4 w-4 mr-1.5" /> Back
          </Button>
          {step < STEPS.length - 1 ? (
            <Button
              data-testid={DEPLOY.nextBtn}
              onClick={() => canNext() && setStep((s) => s + 1)}
              disabled={!canNext()}
              className="bg-slate-900 hover:bg-slate-800 text-white"
            >
              Continue <ArrowRight className="h-4 w-4 ml-1.5" />
            </Button>
          ) : (
            <Button
              data-testid={DEPLOY.submitBtn}
              onClick={submit}
              disabled={submitting}
              className="bg-slate-900 hover:bg-slate-800 text-white"
            >
              {submitting ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Rocket className="h-4 w-4 mr-1.5" />}
              Submit request
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}

function ReviewLine({ label, value }) {
  return (
    <div className="grid grid-cols-3 gap-4 text-sm">
      <div className="text-[10px] font-semibold tracking-[0.08em] uppercase text-slate-500">
        {label}
      </div>
      <div className="col-span-2 text-slate-800">{value || "—"}</div>
    </div>
  );
}


function detectFamily(assets) {
  // Return a set of families present across the selected assets.
  const fams = new Set();
  for (const a of assets || []) {
    const s = ((a.brand || "") + " " + (a.model || "") + " " + (a.category || "")).toLowerCase();
    if (s.includes("apple") || s.includes("mac") || s.includes("iphone") || s.includes("ipad")) fams.add("apple");
    if (s.includes("windows") || s.includes("dell") || s.includes("hp ") || s.includes("lenovo") || s.includes("microsoft")) fams.add("windows");
  }
  if (fams.size === 0) fams.add("any");
  return fams;
}


function ServiceGroup({ group, selectedAssets, form, setForm, toggleService,
                        appleAgreements, msTenants }) {
  const families = detectFamily(selectedAssets);
  // Filter services by device family; keep undefined-family services always
  const visible = group.services.filter((s) => {
    if (!s.family || s.family === "any") return true;
    return families.has(s.family);
  });
  if (visible.length === 0) return null;
  const isSelected = (code) => form.services_selected.includes(code);

  return (
    <div className="border border-slate-200 rounded-md bg-white p-4"
      data-testid={`deploy-group-${group.id}`}>
      <div className="text-[11px] font-semibold tracking-[0.14em] uppercase text-slate-500 mb-3">
        {group.label}
      </div>
      <div className="grid grid-cols-2 gap-2">
        {visible.map((s) => (
          <label key={s.code}
            data-testid={`svc-${s.code}`}
            className={"flex items-center gap-3 border rounded-md px-3 py-2 hover:bg-slate-50 cursor-pointer " +
              (isSelected(s.code)
                ? "border-slate-900 bg-slate-50"
                : "border-slate-200")}>
            <Checkbox checked={isSelected(s.code)}
              onCheckedChange={() => toggleService(s.code)} />
            <span className="text-sm text-slate-800">{s.label}</span>
          </label>
        ))}
      </div>

      {/* Conditional expansion panels — only show when selected */}
      {group.id === "protection" && (
        <div className="mt-3 space-y-3">
          {isSelected("applecare") && (
            <AppleCarePanel form={form} setForm={setForm} agreements={appleAgreements} />
          )}
          {isSelected("third_party_insurance") && (
            <ThirdPartyInsurancePanel form={form} setForm={setForm} />
          )}
          {isSelected("oem_warranty") && (
            <OemWarrantyPanel form={form} setForm={setForm} />
          )}
          {isSelected("accidental_damage") && (
            <AccidentalDamagePanel form={form} setForm={setForm} />
          )}
          {isSelected("transit_insurance") && (
            <TransitInsurancePanel form={form} setForm={setForm} />
          )}
          {isSelected("customer_provided") && (
            <div className="border border-amber-300 bg-amber-50 rounded-md p-3 text-[12px] text-amber-900">
              <strong>CUSTOMER-PROVIDED COVERAGE</strong> — Suntronix is not arranging this coverage.
              Upload proof and record the coverage owner separately in Customer 360.
            </div>
          )}
          {isSelected("no_coverage") && (
            <NoCoveragePanel form={form} setForm={setForm} />
          )}
        </div>
      )}

      {group.id === "mgmt" && (
        <div className="mt-3 space-y-3">
          {[
            "autopilot_registration", "autopilot_deployment",
            "autopilot_pre_provisioning", "autopilot_device_prep",
            "intune_enrolment", "entra_join", "entra_hybrid_join",
          ].some((c) => isSelected(c)) && (
            <WindowsAutopilotPanel form={form} setForm={setForm} tenants={msTenants} />
          )}
        </div>
      )}
    </div>
  );
}


function AppleCarePanel({ form, setForm, agreements }) {
  const activeAgreements = agreements.filter((a) => a.status === "ACTIVE"
    && (!a.plan_type || a.plan_type === "APPLECARE_FOR_ENTERPRISE"));
  const requiresAgreement = form.applecare_plan === "APPLECARE_FOR_ENTERPRISE";
  return (
    <div className="border border-slate-200 rounded-md bg-slate-50/60 p-4 space-y-3"
      data-testid="applecare-panel">
      <div className="text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-600">
        AppleCare & Apple Protection
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <Label className="text-xs">Plan type *</Label>
          <Select value={form.applecare_plan}
            onValueChange={(v) => setForm({ ...form, applecare_plan: v })}>
            <SelectTrigger className="h-10 mt-1" data-testid="applecare-plan"><SelectValue placeholder="Choose plan…" /></SelectTrigger>
            <SelectContent>
              {APPLECARE_PLANS.map((p) =>
                <SelectItem key={p.v} value={p.v}>{p.l}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-xs">Coverage source</Label>
          <Select value={form.applecare_source}
            onValueChange={(v) => setForm({ ...form, applecare_source: v })}>
            <SelectTrigger className="h-10 mt-1"><SelectValue placeholder="Choose source…" /></SelectTrigger>
            <SelectContent>
              {["NEW_TO_BE_PROCURED", "EXISTING_CUSTOMER_COVERAGE",
                "MASTER_CLIENT_AGREEMENT", "CUSTOMER_SUBSIDIARY_AGREEMENT",
                "SUNTRONIX_ARRANGED", "INCLUDED_IN_DEVICE_ORDER", "OTHER"].map((v) =>
                <SelectItem key={v} value={v}>{v.replace(/_/g, " ")}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </div>
      {requiresAgreement && (
        <div>
          <Label className="text-xs">Apple Agreement Number *</Label>
          <Select value={form.apple_agreement_number}
            onValueChange={(v) => setForm({ ...form, apple_agreement_number: v })}>
            <SelectTrigger className="h-10 mt-1" data-testid="applecare-agreement">
              <SelectValue placeholder={activeAgreements.length ? "Pick an active agreement…" : "No active agreements — add one in Customer 360"} />
            </SelectTrigger>
            <SelectContent>
              {activeAgreements.map((a) =>
                <SelectItem key={a.id} value={a.agreement_number}>
                  {a.agreement_number} — {a.agreement_name || "Unnamed"} · {a.effective_until}
                </SelectItem>)}
            </SelectContent>
          </Select>
          {activeAgreements.length === 0 && (
            <div className="text-[11px] text-rose-700 mt-1">
              APPLE AGREEMENT NUMBER REQUIRED — This deployment uses AppleCare for Enterprise.
              Enter or select the applicable customer Apple Agreement Number before continuing.
            </div>
          )}
        </div>
      )}
      {form.applecare_plan === "EXISTING_APPLECARE" && (
        <div>
          <Label className="text-xs">Proof of existing coverage (URL / document ref)</Label>
          <Input className="h-10 mt-1"
            value={form.applecare_existing_proof_url}
            onChange={(e) => setForm({ ...form, applecare_existing_proof_url: e.target.value })}
            placeholder="Upload proof and paste reference here" />
        </div>
      )}
    </div>
  );
}


function OemWarrantyPanel({ form, setForm }) {
  const COV = ["ONSITE", "CARRY_IN", "PARTS", "LABOUR", "ACCIDENTAL_DAMAGE",
    "BATTERY", "KEEP_YOUR_DRIVE", "NEXT_BUSINESS_DAY", "PREMIUM_SUPPORT"];
  const on = (v) => (form.insurance_coverage_types || []).includes(v);
  const toggle = (v) => {
    const set = new Set(form.insurance_coverage_types || []);
    set.has(v) ? set.delete(v) : set.add(v);
    setForm({ ...form, insurance_coverage_types: Array.from(set) });
  };
  return (
    <div className="border border-slate-200 rounded-md bg-slate-50/60 p-4 space-y-3"
      data-testid="oem-panel">
      <div className="text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-600">
        OEM Extended Warranty
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <Label className="text-xs">Manufacturer / provider *</Label>
          <Input className="h-10 mt-1"
            value={form.insurance_provider}
            onChange={(e) => setForm({ ...form, insurance_provider: e.target.value })}
            placeholder="Dell / HP / Lenovo / OEM partner"
            data-testid="oem-provider" />
        </div>
        <div>
          <Label className="text-xs">Plan reference / certificate *</Label>
          <Input className="h-10 mt-1"
            value={form.insurance_policy_number}
            onChange={(e) => setForm({ ...form, insurance_policy_number: e.target.value })}
            data-testid="oem-plan" />
        </div>
      </div>
      <div>
        <div className="text-[10px] uppercase tracking-[0.12em] text-slate-500 mb-1.5">
          Warranty coverage (select all that apply)
        </div>
        <div className="flex flex-wrap gap-1.5">
          {COV.map((v) => (
            <button key={v} type="button" onClick={() => toggle(v)}
              data-testid={`oem-cov-${v}`}
              className={
                "h-7 px-2.5 rounded-full text-[12px] border transition-colors " +
                (on(v) ? "bg-slate-900 text-white border-slate-900"
                  : "bg-white text-slate-700 border-slate-300 hover:bg-slate-50")
              }>
              {v.replace(/_/g, " ")}
            </button>
          ))}
        </div>
      </div>
      <div className="text-[11px] text-slate-500">
        Attach the OEM certificate in ONE Documents after activation.
      </div>
    </div>
  );
}


function AccidentalDamagePanel({ form, setForm }) {
  const RISKS = ["ACCIDENTAL_DAMAGE", "LIQUID_DAMAGE", "SCREEN_DAMAGE",
    "ELECTRICAL_DAMAGE", "THEFT", "LOSS", "FIRE", "NATURAL_CALAMITY"];
  const on = (v) => (form.insurance_coverage_types || []).includes(v);
  const toggle = (v) => {
    const set = new Set(form.insurance_coverage_types || []);
    set.has(v) ? set.delete(v) : set.add(v);
    setForm({ ...form, insurance_coverage_types: Array.from(set) });
  };
  return (
    <div className="border border-slate-200 rounded-md bg-slate-50/60 p-4 space-y-3"
      data-testid="adp-panel">
      <div className="text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-600">
        Accidental Damage Protection
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <Label className="text-xs">Provider *</Label>
          <Input className="h-10 mt-1"
            value={form.insurance_provider}
            onChange={(e) => setForm({ ...form, insurance_provider: e.target.value })}
            data-testid="adp-provider" />
        </div>
        <div>
          <Label className="text-xs">Policy / plan number *</Label>
          <Input className="h-10 mt-1"
            value={form.insurance_policy_number}
            onChange={(e) => setForm({ ...form, insurance_policy_number: e.target.value })}
            data-testid="adp-policy" />
        </div>
      </div>
      <div>
        <div className="text-[10px] uppercase tracking-[0.12em] text-slate-500 mb-1.5">
          Risks covered (do not assume every plan covers every risk)
        </div>
        <div className="flex flex-wrap gap-1.5">
          {RISKS.map((v) => (
            <button key={v} type="button" onClick={() => toggle(v)}
              data-testid={`adp-risk-${v}`}
              className={
                "h-7 px-2.5 rounded-full text-[12px] border transition-colors " +
                (on(v) ? "bg-slate-900 text-white border-slate-900"
                  : "bg-white text-slate-700 border-slate-300 hover:bg-slate-50")
              }>
              {v.replace(/_/g, " ")}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}


function ThirdPartyInsurancePanel({ form, setForm }) {  return (
    <div className="border border-slate-200 rounded-md bg-slate-50/60 p-4 space-y-3"
      data-testid="insurance-panel">
      <div className="text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-600">
        Third-Party Device Insurance
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <Label className="text-xs">Insurance arrangement *</Label>
          <Select value={form.insurance_arrangement}
            onValueChange={(v) => setForm({ ...form, insurance_arrangement: v })}>
            <SelectTrigger className="h-10 mt-1" data-testid="insurance-arrangement">
              <SelectValue placeholder="Choose arrangement…" />
            </SelectTrigger>
            <SelectContent>
              {INSURANCE_ARRANGEMENTS.map((a) =>
                <SelectItem key={a.v} value={a.v}>{a.l}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-xs">Insurance provider *</Label>
          <Input className="h-10 mt-1"
            value={form.insurance_provider}
            onChange={(e) => setForm({ ...form, insurance_provider: e.target.value })}
            data-testid="insurance-provider" />
        </div>
        <div>
          <Label className="text-xs">Policy number *</Label>
          <Input className="h-10 mt-1"
            value={form.insurance_policy_number}
            onChange={(e) => setForm({ ...form, insurance_policy_number: e.target.value })}
            data-testid="insurance-policy" />
        </div>
        <div>
          <Label className="text-xs">Sum insured (₹) *</Label>
          <Input type="number" min="0" className="h-10 mt-1"
            value={form.insurance_sum_insured}
            onChange={(e) => setForm({ ...form, insurance_sum_insured: e.target.value })}
            data-testid="insurance-sum" />
        </div>
      </div>
    </div>
  );
}


function TransitInsurancePanel({ form, setForm }) {
  return (
    <div className="border border-slate-200 rounded-md bg-slate-50/60 p-4 space-y-3"
      data-testid="transit-panel">
      <div className="text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-600">
        Shipment / Transit Insurance
      </div>
      <div className="grid grid-cols-3 gap-3">
        <div>
          <Label className="text-xs">Declared value (₹) *</Label>
          <Input type="number" min="0" className="h-10 mt-1"
            value={form.transit_declared_value}
            onChange={(e) => setForm({ ...form, transit_declared_value: e.target.value })}
            data-testid="transit-declared" />
        </div>
        <div>
          <Label className="text-xs">Insured value (₹) *</Label>
          <Input type="number" min="0" className="h-10 mt-1"
            value={form.transit_insured_value}
            onChange={(e) => setForm({ ...form, transit_insured_value: e.target.value })}
            data-testid="transit-insured" />
        </div>
        <div>
          <Label className="text-xs">Policy / certificate number *</Label>
          <Input className="h-10 mt-1"
            value={form.transit_policy_number}
            onChange={(e) => setForm({ ...form, transit_policy_number: e.target.value })}
            data-testid="transit-policy" />
        </div>
      </div>
      <div className="text-[11px] text-slate-500">
        Dispatch is blocked when customer policy requires transit insurance and coverage is missing.
      </div>
    </div>
  );
}


function NoCoveragePanel({ form, setForm }) {
  const reasons = [
    { v: "CUSTOMER_DECLINED", l: "Customer declined" },
    { v: "EXISTING_COVERAGE_CONFIRMED", l: "Existing coverage confirmed" },
    { v: "NOT_REQUIRED_BY_CUSTOMER_POLICY", l: "Not required by customer policy" },
    { v: "DEVICE_NOT_ELIGIBLE", l: "Device not eligible" },
    { v: "COST_NOT_APPROVED", l: "Cost not approved" },
    { v: "OTHER", l: "Other approved reason" },
  ];
  return (
    <div className="border border-slate-200 rounded-md bg-slate-50/60 p-4 space-y-3"
      data-testid="no-coverage-panel">
      <div className="text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-600">
        No Additional Coverage
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <Label className="text-xs">Reason *</Label>
          <Select value={form.no_coverage_reason}
            onValueChange={(v) => setForm({ ...form, no_coverage_reason: v })}>
            <SelectTrigger className="h-10 mt-1" data-testid="no-coverage-reason"><SelectValue placeholder="Choose…" /></SelectTrigger>
            <SelectContent>
              {reasons.map((r) => <SelectItem key={r.v} value={r.v}>{r.l}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-xs">Approved by</Label>
          <Input className="h-10 mt-1"
            value={form.no_coverage_approved_by}
            onChange={(e) => setForm({ ...form, no_coverage_approved_by: e.target.value })}
            placeholder="approver@customer.com" />
        </div>
      </div>
    </div>
  );
}


function WindowsAutopilotPanel({ form, setForm, tenants }) {
  const pickTenant = (id) => {
    const t = tenants.find((x) => x.id === id);
    setForm({ ...form, ms_tenant_pick: id,
      ms_tenant_id: t?.tenant_id || "",
      ms_primary_domain: t?.primary_domain || "" });
  };
  return (
    <div className="border border-slate-200 rounded-md bg-slate-50/60 p-4 space-y-3"
      data-testid="autopilot-panel">
      <div className="text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-600">
        Microsoft Autopilot / Intune / Entra
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <Label className="text-xs">Customer Microsoft tenant *</Label>
          <Select value={form.ms_tenant_pick} onValueChange={pickTenant}>
            <SelectTrigger className="h-10 mt-1" data-testid="ms-tenant-pick">
              <SelectValue placeholder={tenants.length ? "Pick a verified tenant…" : "No tenants yet — add one in Customer 360"} />
            </SelectTrigger>
            <SelectContent>
              {tenants.map((t) =>
                <SelectItem key={t.id} value={t.id}>
                  {t.tenant_display_name} · {t.primary_domain}
                </SelectItem>)}
            </SelectContent>
          </Select>
          {form.ms_tenant_id && (
            <div className="text-[10px] text-slate-500 mt-1 font-mono">
              Tenant ID: {form.ms_tenant_id}
              <span className="ml-2 text-emerald-700">VALUE SOURCE: Customer Microsoft Standard</span>
            </div>
          )}
        </div>
        <div>
          <Label className="text-xs">Primary domain *</Label>
          <Input className="h-10 mt-1"
            value={form.ms_primary_domain}
            readOnly={!!form.ms_tenant_pick}
            onChange={(e) => setForm({ ...form, ms_primary_domain: e.target.value })}
            data-testid="ms-primary-domain" />
        </div>
        <div>
          <Label className="text-xs">Autopilot service required</Label>
          <Select value={form.autopilot_service_required}
            onValueChange={(v) => setForm({ ...form, autopilot_service_required: v })}>
            <SelectTrigger className="h-10 mt-1" data-testid="ap-service"><SelectValue placeholder="Choose…" /></SelectTrigger>
            <SelectContent>
              {AUTOPILOT_SERVICES.map((s) =>
                <SelectItem key={s} value={s}>{s.replace(/_/g, " ")}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-xs">Registration method</Label>
          <Select value={form.registration_method}
            onValueChange={(v) => setForm({ ...form, registration_method: v })}>
            <SelectTrigger className="h-10 mt-1"><SelectValue placeholder="Choose…" /></SelectTrigger>
            <SelectContent>
              {REGISTRATION_METHODS.map((s) =>
                <SelectItem key={s} value={s}>{s.replace(/_/g, " ")}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-xs">Deployment method</Label>
          <Select value={form.deployment_method}
            onValueChange={(v) => setForm({ ...form, deployment_method: v })}>
            <SelectTrigger className="h-10 mt-1"><SelectValue placeholder="Choose…" /></SelectTrigger>
            <SelectContent>
              {DEPLOYMENT_METHODS.map((s) =>
                <SelectItem key={s} value={s}>{s.replace(/_/g, " ")}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-xs">Entra join type</Label>
          <Select value={form.entra_join_type}
            onValueChange={(v) => setForm({ ...form, entra_join_type: v })}>
            <SelectTrigger className="h-10 mt-1"><SelectValue placeholder="Choose…" /></SelectTrigger>
            <SelectContent>
              {ENTRA_JOIN_TYPES.map((s) =>
                <SelectItem key={s} value={s}>{s.replace(/_/g, " ")}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-xs">Autopilot profile ID</Label>
          <Input className="h-10 mt-1"
            value={form.autopilot_profile_id}
            onChange={(e) => setForm({ ...form, autopilot_profile_id: e.target.value })}
            placeholder="Profile ID or name" />
        </div>
        <div>
          <Label className="text-xs">Group tag</Label>
          <Input className="h-10 mt-1"
            value={form.autopilot_group_tag}
            onChange={(e) => setForm({ ...form, autopilot_group_tag: e.target.value })}
            placeholder="e.g. IND-SALES-LAPTOP" />
        </div>
        <div>
          <Label className="text-xs">Assigned user UPN</Label>
          <Input className="h-10 mt-1"
            value={form.user_upn}
            onChange={(e) => setForm({ ...form, user_upn: e.target.value })}
            placeholder="employee@customer.com" />
        </div>
        <div>
          <Label className="text-xs">Customer authorisation status</Label>
          <Select value={form.customer_authorisation_status}
            onValueChange={(v) => setForm({ ...form, customer_authorisation_status: v })}>
            <SelectTrigger className="h-10 mt-1" data-testid="ap-authorisation"><SelectValue /></SelectTrigger>
            <SelectContent>
              {AUTHORISATION_STATUSES.map((s) =>
                <SelectItem key={s} value={s}>{s.replace(/_/g, " ")}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </div>
      <div className="text-[11px] text-slate-500 border-t border-slate-200 pt-2">
        Never enter Microsoft passwords, tokens or client secrets. Use approved consent flows.
      </div>
    </div>
  );
}


function PreflightPanel({ result, onDismiss }) {
  const { blockers = [], warnings = [], ok } = result || {};
  return (
    <div className={"border rounded-md p-4 " +
      (ok ? "border-emerald-300 bg-emerald-50" : "border-rose-300 bg-rose-50")}
      data-testid="preflight-panel">
      <div className="flex items-center justify-between">
        <div className={"text-[12px] font-semibold tracking-[0.1em] uppercase " +
          (ok ? "text-emerald-800" : "text-rose-800")}>
          {ok ? "Preflight passed" : `Preflight blocked — ${blockers.length} issue(s)`}
        </div>
        <button onClick={onDismiss} className="text-[11px] text-slate-500 hover:text-slate-900">
          Dismiss
        </button>
      </div>
      {blockers.length > 0 && (
        <ul className="mt-2 space-y-1">
          {blockers.map((b, i) => (
            <li key={i} className="text-[12px] text-rose-800" data-testid={`preflight-blocker-${b.code}`}>
              <span className="font-mono text-[10px] mr-2 uppercase">{b.code}</span>
              {b.message}
            </li>
          ))}
        </ul>
      )}
      {warnings.length > 0 && (
        <ul className="mt-2 space-y-1 border-t border-slate-200 pt-2">
          {warnings.map((w, i) => (
            <li key={i} className="text-[11px] text-amber-800">
              <span className="font-mono text-[10px] mr-2 uppercase">{w.code}</span>
              {w.message}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
